import { HeaderOptions } from "components/Header/HeaderUI"
import { ParentsV2_Awards } from "components/Strapi/parents-v2/Awards"
import { ParentsV2_BigIconsHero } from "components/Strapi/parents-v2/ComponentHeroBigIconsHero"
import { ParentsV2_EngagingBanner } from "components/Strapi/parents-v2/EngagingBanner"
import { ParentsV2_FAQSection } from "components/Strapi/parents-v2/FAQSection"
import { ParentsV2_HowItWorks } from "components/Strapi/parents-v2/HowItWorks"
import { ParentsV2_JoshQuote } from "components/Strapi/parents-v2/JoshQuote"
import { ParentsV2_ProductsHighlights } from "components/Strapi/parents-v2/ProductHighlights"
import { ParentsV2_RatingsHero } from "components/Strapi/parents-v2/RatingsHero"
import { ParentsV2_RecommendedByTeachers } from "components/Strapi/parents-v2/RecommendedByTeachers"
import { ParentV2_StartReadingAdventure } from "components/Strapi/parents-v2/StartReadingAdventure"
import { graphql, navigate } from "gatsby"
import React from "react"
import styled from "styled-components"
import getLocales from "utils/getLocales"
import FooterStrapi from "../components/Footer"
import SEO, { getSEOProps } from "../components/SEO"
import { EU_LOCALES } from "../constants"
import BareLayout from "../layouts/Bare"

import '@fontsource/nunito/400.css'
import '@fontsource/nunito/900.css'
import '@fontsource/baloo-2/400.css'
import HeaderStrapi from "components/Header"
import { TrustpilotWidgetBase } from "components/TrustpilotWidgets"
import { StaticImage } from 'gatsby-plugin-image'
import { ParentsV2_CTA } from "components/Strapi/parents-v2/CTA"

const Wrapper = styled.div`
  background: #ece7f0;
  color: #351778;
  font-family: "Baloo 2";
  overflow: hidden;
  max-width: 100vw;

  h1,h2,h3,h4 {
    font-family: "Nunito";
    font-weight: 900 !important;
  }
  h2 {
    font-size: 2em !important;
    color: #351778;
  }
  > :first-child {
    padding-top: 50px;
  }

  &.with-tp-banner,
  &.sticky-tp {
    > :first-child {
      padding-top: 90px;
    }
  }

  .desktop {
    display: block !important;
  }
  .mobile {
    display: none !important;
  }

  @media screen and (min-width: 1180px) {
    .desktop{ display: block !important; }
    .mobile{ display: none !important; }
  }
  @media screen and (max-width: 1179px) {
    .mobile{ display: block !important; }
    .desktop{ display: none !important; }
  }
`

interface IProps {
  data: {
    site: {
      siteMetadata: { siteUrl: string }
    }
    strapiDynamicPages: Strapi.DynamicPage
  }
  pageContext: {
    defaultLang: string
    slug: string
    pagePath?: string
    noCountrySelector?: boolean
    headerOptions?: HeaderOptions
  }
}

export const updateLocalizations = page => {
  const { locale, localizations } = page
  const locales = [...localizations].map(l => l.locale)
  if (
    EU_LOCALES.indexOf(locale) >= 1 ||
    localizations.filter(l => EU_LOCALES.indexOf(l.locale) >= 1).length >= 1
  ) {
    return {
      locale,
      localizations: [
        ...localizations,
        ...EU_LOCALES.filter(l => locales.indexOf(l) < 0 && l !== locale).map(
          l => ({
            locale: `en-${l}`,
            translated: false,
          })
        ),
      ],
    }
  }
  return { locale, localizations }
}

const ParentPageV2Template = (props: IProps) => {
  const { data, pageContext }: { data: any; pageContext: any } = props
  const { site, strapiDynamicpages: dynamicpage } = data
  const { content, SEO: SEOProps, customPath } = dynamicpage
  const { locale, localizations = [] } = updateLocalizations({
    ...dynamicpage,
    localizations: [
      ...dynamicpage.localizations,
      { locale: dynamicpage.locale, translated: dynamicpage.translated },
    ],
    locale: pageContext.locale
      ? pageContext.locale.replace("/", "")
      : dynamicpage.locale,
  })
  const otherLangs = getLocales(localizations)
  const nonStickyTrustPilotbanner =
    pageContext.headerOptions?.trustpilot &&
    !pageContext.headerOptions.trustpilot.sticky
  const stickyTrustpilot = Boolean(
    pageContext.headerOptions?.trustpilot?.sticky
  )

  return (
    <BareLayout>
      <SEO
        {...getSEOProps(SEOProps)}
        lang={locale}
        links={otherLangs.map(l => {
          if (l === props.pageContext.defaultLang || l === "en") {
            return {
              rel: "alternate",
              hrefLang: l === "en" ? "x-default" : l,
              href: `${site.siteMetadata.siteUrl}${customPath ? `${customPath}` : `/${props.pageContext.slug}`
                }`,
            }
          }
          return {
            rel: "alternate",
            hrefLang: l,
            href: `${site.siteMetadata.siteUrl}/${l}${customPath ? `${customPath}` : `/${props.pageContext.slug}`
              }`,
          }
        })}
        canonical={`${site.siteMetadata.siteUrl}${locale === "en" ? "" : `/${locale}`
          }${customPath ? `${customPath}` : `/${props.pageContext.slug}`}`}
        titleTemplate="%s | Night Zookeeper"
      />
      <HeaderStrapi
        availableLangs={props.pageContext.noCountrySelector ? [] : otherLangs}
        locale={locale}
        pagePath={props.pageContext?.pagePath}
        options={{
          ...(props.pageContext?.headerOptions || {}),
          trialCta: {
            disabled: false,
            label: 'Start Free Trial',
            onClick: () => {
              navigate('/parents/checkout/plan')
            }
          }
        }}
      />
      <Wrapper
        className={`content-root ${nonStickyTrustPilotbanner ? "with-tp-banner" : ""
          } ${stickyTrustpilot ? "sticky-tp" : ""}`}
      >
        <ParentsV2_EngagingBanner
          cta={{
            label: 'Start Free Trial',
            url: '/parents/checkout/plan'
          }}
        />
        <ParentsV2_HowItWorks
          title='How it works'
          description={<>Words. Sentences. Stories.<br />Every step builds your child's creativity, confidence, and voice.</>}
          keypoints={[
            {
              title: 'Easy learning',
              description: 'Simple for children to use and learn by themselves',
              iconUrl: '/images/parents-v2/purple-by-educators-icon.svg',
            },
            {
              title: 'Fantastically fun',
              description: 'Creative learning that sparks a love for reading and writing',
              iconUrl: '/images/parents-v2/purple-age-icon.svg',
            },
            {
              title: 'Safe to use',
              description: 'Ad-free and moderated by qualified tutors',
              iconUrl: '/images/parents-v2/purple-safe-icon.svg'
            },
          ]}
          videoUrl='https://cdn.nightzookeeper.com/strapi/media/Nightzookeeper_com_Overview_Intro_Video_2023_b0df28c53f/Nightzookeeper_com_Overview_Intro_Video_2023_b0df28c53f.mp4'
        />
        <ParentsV2_RatingsHero
          title='Loved by 200,000+ children & parents'
          quotes={[
            {
              text: "Night Zookeeper turned eye-rolls into enthusiasm! Now my kids love writing, spelling, and grammar—even on Saturdays!",
              author: { name: "Asha", type: "Parent" },
            },
            {
              text: "My daughter loves Night Zookeeper so much she'd rather 'play' than do anything else! She’s always loved reading—now she loves writing too!",
              author: { name: "Jeremiah", type: "Parent" },
            },
            {
              text: "My kids are writing more than ever! The game and rewards keep them engaged, and their creativity is still flowing after 5 weeks!",
              author: { name: "Paul", type: "Parent" },
            },
          ]}
          cta={{
            label: 'Start Free Trial',
            url: '/parents/checkout/plan'
          }}
        />
        <ParentsV2_BigIconsHero
          title='Why parents pick Night Zookeeper'
          icons={[
            { icon: <img src='/images/parents-v2/purple-safe-icon.svg' />, text: 'A safe, moderated environment', },
            { icon: <img src='/images/parents-v2/purple-motivate-icon.svg' />, text: 'Engaging and motivating', },
            { icon: <img src='/images/parents-v2/purple-by-educators-icon.svg' />, text: 'Developed by experienced educators', },
            { icon: <img src='/images/parents-v2/purple-tutor-feedback-icon.svg' />, text: 'Personalized tutor feedback', },
            { icon: <img src='/images/parents-v2/purple-play-icon.svg' />, text: 'Learn through play', },
            { icon: <img src='/images/parents-v2/purple-age-icon.svg' />, text: 'Perfect for ages 6-12', },
          ]}
        />

        <ParentsV2_FAQSection
          title={"Frequently Asked Questions"}
          questions={[
            {
              "title": "How can something as fun as Night Zookeeper also be so educational?",
              "answer": "Night Zookeeper is a full reading and writing curriculum designed as an adventure! Kids improve grammar, vocabulary, spelling, reading comprehension, and creative writing skills while having fun. Parents often say, “My child didn’t even realize they were learning!”"
            },
            {
              "title": "Will my child actually want to do this, or will it feel like schoolwork? ",
              "answer": "Most kids ask to play Night Zookeeper, even after other schoolwork is done. The world is imaginative, interactive, and reward-based, which keeps them excited to come back and write more."
            },
            {
              "title": "Can I try it before I commit?",
              "answer": "Definitely. You can start with a free trial to see how your child responds. We understand that every child is unique, so if you find that the program isn’t the right fit for your child, you can cancel any time within the 7-day trial period - your card won’t be charged. No questions asked, no strings attached!"
            },
            {
              "title": "What will my child write about?",
              "answer": "They’ll write about everything, from stories about magical animals to instructions on how to make the perfect hot chocolate. All genres of writing are gradually introduced, including newspaper articles, persuasive writing, reports, and poetry."
            },
            {
              "title": "Can my child use this independently? Or will I need to help my child the whole time?",
              "answer": "Night Zookeeper is designed for independent learning. Most children can navigate the program on their own, making it perfect for busy parents or homeschoolers needing some independent study time. You can always check in using the parent dashboard to stay updated on their progress."
            },
            {
              "title": " What devices does Night Zookeeper work on?",
              "answer": "Night Zookeeper works across tablets, PC’s and laptops. We also have an iOS app available on iPad and iPhone. For other devices, we recommend using Night Zookeeper on Safari or Google Chrome browsers for optimal performance."
            },
            {
              "title": " Is this safe for my child to use online?",
              "answer": "Yes. Night Zookeeper is fully moderated, ad-free, and designed to be a safe space for children. There’s no open chat, and a qualified tutor reviews every piece of writing before it is published."
            },
            {
              "title": "What ages are best for this program?",
              "answer": "Night Zookeeper is best for ages 6 to 12. When you join, you’ll be able to select the age of your child, and this will change the type of lessons, challenges, games, and level of vocabulary that they will encounter. You can always adjust this in your parent dashboard."
            },
            {
              "title": " How often should we use Night Zookeeper?",
              "answer": "Children should try to use Night Zookeeper for a minimum of 30 minutes a week to get the best out of the program. This will give them time to complete their weekly assignments and check feedback from their tutors. But there is SO much to do on Night Zookeeper so we recommend a few hours a week to really take advantage of all the material on offer, and to help your child improve their reading and writing faster."
            },
            {
              "title": "Do you have a family subscription?",
              "answer": "Yes, you can add more than one child to your account. Simply press 'Start Free Trial' on a plan, and then add the number of children you would like (up to 3) in the checkout. The total will be displayed on the screen, and there are discounts applied for every child added."
            },
            {
              "title": "Can I track my child’s progress?",
              "answer": "You can track your child’s progress through the parent dashboard. You can easily monitor your child’s writing, achievements, and the feedback they are receiving from their tutor. Night Zookeeper is designed to help your child learn independently, while you can follow along."
            }
          ]} />
        <ParentsV2_CTA
          cta={{
            label: 'Start Free Trial',
            url: '/parents/checkout/plan'
          }}
        />
        <ParentsV2_ProductsHighlights

          title='The best kind of screen time for your child'
          highlights={[
            {
              img: <StaticImage placeholder="blurred" src='../images/parents-v2/1-inspired by own creations.png' alt='Inspired by their own creations' layout="constrained" width={800} />,
              title: 'Inspired by their own creations',
              text: 'Children draw their own magical characters that go with them through the Night Zoo, developing and growing their creative writing skills.',
            },
            {
              img: <StaticImage placeholder="blurred" src='../images/parents-v2/2-Growing a rich vocab-01.png' alt='Growing a rich vocabulary' layout="constrained" width={800} />,
              title: 'Growing a rich vocabulary',
              text: 'The foundation of all great writers is their broad and rich vocabulary. We help your children learn and spell thousands of new words through exciting, personalized activities and games.',
            },
            {
              img: <StaticImage placeholder="blurred" src='../images/parents-v2/2-Growing a rich vocab-02.png' alt='Developing grammar skills' layout="constrained" width={800} />,
              title: 'Developing grammar skills',
              text: "As children progress, they'll encounter sentence building games, short writing challenges and many more exciting activities that help them to use these new words in longer, more complex sentences.",
            },
            {
              img: <StaticImage placeholder="blurred" src='../images/parents-v2/4-Write creative stories.png' alt='Writing creative stories' layout="constrained" width={800} />,
              title: 'Writing creative stories',
              text: 'Children are then challenged to put their learning into action, producing stories, reports, poems and other styles of writing. Their work will live in a personal portfolio that can be shared with family, friends, and a global audience!',
            },
            {
              img: <StaticImage placeholder="blurred" src='../images/parents-v2/5-Tutor Feedback.png' alt='Feedback from qualified tutors' layout="constrained" width={800} />,
              title: 'Feedback from qualified tutors',
              text: 'To take their writing to the next level, children will receive personalized, encouraging feedback from our team of qualified tutors. You can check the feedback your child gets in your parent dashboard.',
            },
            {
              img: <StaticImage placeholder="blurred" src='../images/parents-v2/6-Parent Tracking.png' alt='Easily track progress' layout="constrained" width={800} />,
              title: 'Easily track progress',
              text: "From your parent dashboard you can track your child's progress, view tutor feedback and areas to improve, as well as customize their learning experience with your own assignments.",
            }
          ]}
        />
        <ParentV2_StartReadingAdventure
          title="Start your child's reading and writing adventure"
          points={[
            {
              title: "Endless learning and fun",
              text: "Thousands of educational games, lessons & activities."
            },
            {
              title: "Monthly printables",
              text: "Receive monthly printable worksheets for handwriting practice."
            },
            {
              title: "Track progress and set assignments",
              text: "Follow your child’s growth in real time. Set your own assignments or let us do it for you."
            },
          ]}
          cta={{
            label: 'Start Free Trial',
            url: '/parents/checkout/plan'
          }}
        />
        <ParentsV2_JoshQuote
          quote='I created Night Zookeeper to inspire children to be confident &
            creative readers and writers. Through interactive games, engaging
            stories, and curriculum-aligned content, we make learning fun and
            help each child express their unique view of the world.'
          author={{ name: "Joshua Davidson", description: "Co-Founder & Author of our Book Series" }}
        />
        <ParentsV2_RecommendedByTeachers
          title='Recommended and trusted by teachers'
          description=''
          quotes={[
            {
              text: "This is the best interactive writing tool I've come across in 24 years.",
              teacher: { name: 'Laura Seiser', school: 'Northeast Hamilton School, USA' }
            },
            {
              text: "Night Zookeeper captures the children's imaginations and can promote a positive reading and writing experience - in and out of school. We love it!",
              teacher: { name: 'Emma Cotton', school: 'Langham Village School, UK' }
            },
            {
              text: "Night Zookeeper has been a game changer for my students' writing. They write more and have improved significantly as a result of using this engaging platform.",
              teacher: { name: "Matt Peebles", school: "Forest Elementary, USA" }
            }
          ]}
        />
        <ParentsV2_Awards
          description="Independently recognized by educators and homeschool experts for excellence in children's reading & writing development"
          awards={[
            {
              title: "Best Homeschool Curriculum & Resources\n2026",
              img: <StaticImage placeholder="blurred" src='../images/awards/best-homeschool-curriculum.webp' alt='How To Homeschool Image' layout="constrained" width={80} />,
              name: "How To Homeschool",
            },
            {
              title: "Best of the Best\n2026",
              img: <StaticImage placeholder="blurred" src='../images/awards/best-of-the-best.webp' alt='Homeschool Awards Image' layout="constrained" width={80} />,
              name: "Homeschool Awards",
            },
            {
              title: "Certified\n2024",
              img: <StaticImage placeholder="blurred" src='../images/awards/certified.webp' alt='Educational App Store Image' layout="constrained" width={80} />,
              name: "Educational App Store",
            },
            {
              title: "i-Learn Award Winner\n2024",
              img: <StaticImage placeholder="blurred" src='../images/awards/i-learn.webp' alt='Practical Homeschooling Image' layout="constrained" width={80} />,
              name: "Practical Homeschooling",
            },
            {
              title: "Recommended Teachers Choice\n2021",
              img: <StaticImage placeholder="blurred" src='../images/awards/edtech-impact.webp' alt='EdTech Impact Image' layout="constrained" width={80} />,
              name: "EdTech Impact",
            },
            {
              title: "Top 102 Picks For Homeschool Curriculum",
              img: <StaticImage placeholder="blurred" src='../images/awards/top-102.webp' alt='Cathy Duffy Image' layout="constrained" width={80} />,
              name: "Cathy Duffy",
            },
            {
              title: "Learning Award\n2020",
              img: <StaticImage placeholder="blurred" src='../images/awards/learning.webp' alt='Common Sense Media Image' layout="constrained" width={80} />,
              name: "Common Sense Media",
            },
          ]}
          cta={{
            label: 'Start Free Trial',
            url: '/parents/checkout/plan'
          }}
        />

        <TrustpilotWidgetBase
          locale="en-GB"
          templateId="539ad60defb9600b94d7df2c"
          businessUnitId="5e99bfb13e39470001f6ee60"
          height="500px"
          stars='5'
          width="100%"
          theme="light"
          fontFamily='Libre Baskerville'
        />
      </Wrapper>
      <FooterStrapi />
    </BareLayout>
  )
}

export const query = graphql`
  query ($id: Int) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    strapiHomepage {
      header {
        links {
          link {
            placeholder
            target
            external
            id
          }
        }
      }
    }
    strapiDynamicpages(strapiId: { eq: $id }) {
      locale
      customPath
      translated
      localizations {
        locale
        customPath
        translated
      }
      content {
        ...BookHero
        ...ZooHero
        ...ParentProductHero
        ...TeacherHero
        ...ShopHero
        ...PartnersGroup
        ...ExplanationHero
        ...TitleSeparator
        ...EngagingBanner
        ...BigIconsHero
        ...RatingsHero
        ...HowItWorks
        ...QuoteHero
        ...PlansHero
        ...PlanHero
        ...QuotesCarousel
        ...TeacherCTAHero
        ...PageTitle
        ...FAQSection
        ...Partners
        ...Strapi_RichSection
        ...FixedBanner
        ...CTAStrip
        ...TrustpilotWidget
        ...PlansSelector
        ...LandingPageHero
        __typename
      }
      slug
      SEO {
        title
        description
        url
        noIndex
        image {
          url
        }
        canonical
      }
    }
  }
`

export default ParentPageV2Template
